import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Container } from "@/components/Container";
import { SectionHeading } from "@/components/SectionHeading";
import QuestionCredibilityBlock from "@/components/QuestionCredibilityBlock";
import { CONTACT_EMAIL } from "@/data/content";

const Section = ({ title, children }) => (
  <section className="mt-8 border-t border-[color:rgba(10,27,51,0.10)] pt-8 first:mt-0 first:border-t-0 first:pt-0">
    <h2 className="font-[var(--font-heading)] text-xl font-semibold text-[var(--ibp-ink)]">
      {title}
    </h2>
    <div className="mt-3 space-y-3 text-sm leading-7 text-[color:rgba(11,18,32,0.72)]">
      {children}
    </div>
  </section>
);

export default function About() {
  return (
    <div className="bg-[var(--ibp-offwhite)]">
      <section className="bg-[var(--ibp-navy-950)] py-12 text-white sm:py-16">
        <Container>
          <SectionHeading
            tone="navy"
            kicker="About"
            title="About Illinois Broker Prep"
            subtitle="An independent, state-specific study tool operated by Coursewright."
          />
        </Container>
      </section>

      <section className="py-14 sm:py-20">
        <Container>
          <div className="mx-auto max-w-4xl rounded-2xl border border-[color:rgba(10,27,51,0.12)] bg-white p-7 shadow-[0_10px_30px_rgba(7,20,39,0.06)] sm:p-9">
            <Section title="What Illinois Broker Prep is">
              <p>
                Illinois Broker Prep is an independent study tool for people
                preparing for the Illinois real estate broker licensing exam.
                It is built and operated by Coursewright, a company focused on
                one thing: state-specific exam practice that helps candidates
                walk in ready.
              </p>
            </Section>

            <Section title="Why we're state-specific">
              <p>
                Generic national prep skips what each state actually tests.
                Illinois candidates face a dedicated state-law section: agency,
                license law, escrow, disclosure, and other details where a lot
                of scores are won or lost.
              </p>
              <p>
                Instead of one national question dump, we build focused,
                state-specific practice. Illinois Broker Prep is our Illinois
                tool, and the same approach can extend to other states over
                time.
              </p>
            </Section>

            <Section title="How we build our questions">
              <QuestionCredibilityBlock className="mt-2" />
            </Section>

            <Section title="What this is — and what it isn't">
              <p>
                We think being clear is part of being trustworthy. Illinois
                Broker Prep is an independent study aid. It is not affiliated
                with PSI, IDFPR, the State of Illinois, or any real estate
                commission. It does not contain real exam questions, and it does
                not replace the required 75-hour pre-license course.
              </p>
              <p>
                No prep tool can guarantee a passing score, and we do not claim
                to.
              </p>
            </Section>

            <Section title="How we stand behind it">
              <p>
                Your access is lifetime — yours until you pass — with no
                subscription and free updates as the question bank grows. If it
                is not the right fit, we offer a 7-day no-questions-asked
                refund.
              </p>
              <p>
                There is a real support inbox behind{" "}
                <a
                  href={`mailto:${CONTACT_EMAIL}`}
                  className="font-semibold text-[#0F3D8A] hover:underline"
                >
                  {CONTACT_EMAIL}
                </a>
                , and we aim to reply within one business day.
              </p>
            </Section>

            <div className="mt-8 rounded-2xl border border-[color:rgba(214,162,30,0.25)] bg-[color:rgba(214,162,30,0.08)] p-5 text-sm leading-6 text-[color:rgba(11,18,32,0.76)]">
              Illinois Broker Prep is a Coursewright study tool, operated by
              Coursewright.
            </div>

            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <Link
                to="/product"
                className="inline-flex min-h-11 items-center justify-center gap-2 rounded-xl bg-[var(--ibp-gold-500)] px-5 py-3 text-sm font-semibold text-[var(--ibp-ink)] transition-colors hover:bg-[var(--ibp-primary-hover)]"
              >
                See the study tool <ArrowRight className="h-4 w-4" />
              </Link>
              <Link
                to="/diagnostic"
                className="inline-flex min-h-11 items-center justify-center gap-2 rounded-xl border border-[color:rgba(10,27,51,0.18)] bg-white px-5 py-3 text-sm font-semibold text-[var(--ibp-ink)] transition-colors hover:bg-[var(--ibp-offwhite)]"
              >
                Try 10 questions free
              </Link>
            </div>
          </div>
        </Container>
      </section>
    </div>
  );
}
